import { redirect } from '@tanstack/react-router'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { Jwt, Thread, UIMessage } from '~/model'

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

export function getJwt() {
	const token = localStorage.getItem('token')
	if (token == null || token.length === 0) {
		redirect({
			to: '/no-token',
			throw: true,
		})
	}
	return decodeJwt(token)
}

export function decodeJwt(token: string | null | undefined) {
	if (token == null || token.length === 0) {
		redirect({
			to: '/no-token',
			throw: true,
		})
		throw new Error('Token is null or undefined')
	}

	// Split the token into three parts
	const parts = token.split('.')

	// Check if the token has the correct format
	if (parts.length !== 3) throw new Error('Token does not have the correct format')

	// Decode the base64url-encoded header and payload
	const header = JSON.parse(atob(parts[0])) as unknown
	const payload = JSON.parse(atob(parts[1])) as unknown

	const jwt = {
		header,
		payload,
	} as Jwt

	return jwt
}

export function isStudent(): boolean {
	return getJwt().payload.role === 'student'
}

export function convertBytesNumberToString(bytes: number): string {
	const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
	const exponent = Math.floor(Math.log(bytes) / Math.log(1024))
	const value = bytes / 1024 ** exponent

	return Number.isInteger(value) ? `${value}${units[exponent]}` : `${value.toFixed(1)}${units[exponent]}`
}

export function convertBytesStringToNumber(bytesString: string): number {
	const match = /^(\d+(?:\.\d+)?)\s*([KMGTPEZY]?B|[BKMGTPEZY]|)$/i.exec(bytesString)

	if (!match) {
		throw new Error('Invalid bytes string format')
	}

	const value = parseFloat(match[1])
	const unit = match[2].toUpperCase()

	const units = {
		B: 1,
		KB: 1024,
		MB: 1024 ** 2,
		GB: 1024 ** 3,
		TB: 1024 ** 4,
		PB: 1024 ** 5,
		EB: 1024 ** 6,
		ZB: 1024 ** 7,
		YB: 1024 ** 8,
	} as Record<string, number | undefined>

	const multiplier: number = units[unit] ?? 1

	return Math.round(value * multiplier)
}

export function copyThreadToClipboard(thread: Thread) {
	const string = thread.uiMessages.reduce((build, message) => {
		build = build + (message.role === 'assistant' ? 'Learning Assistant' : 'User') + ': \n'

		build = build + message.content + '\n\n'
		if (message.sources != null && message.sources.length > 0) {
			build = build + 'Sources:\n'
			build = build + message.sources.map((source) => `${source.title} ${source.source}`).join('\n') + '\n\n'
		}
		return build
	}, '')
	void navigator.clipboard.writeText(string)
}

export function messageToClipboardString(message: UIMessage) {
	let string = message.content
	if (message.sources != null && message.sources.length > 0) {
		string += '\nSources:\n'
		string += message.sources.map((source) => `${source.title} ${source.source}`).join('\n') + '\n\n'
	}
	return string
}

export function convertIsoDateToRevisionsFormat(dateString: string): string {
	const date = new Date(dateString)

	const options: Intl.DateTimeFormatOptions = {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	}

	return date.toLocaleString('en-US', options)
}
